import { fustionColorPalette } from "../constant/fusion-color-pallete";

const palette = fustionColorPalette;

export const defaultThemingDark = {
  page: {
    backgroundUrl:
      "https://www.directv.com/dtvassets/global/component-assets/in-page_clup/channel_background.jpg",
    backgroundColor: "#102641",
    theme: "onDark"
  },
  categoriesTab: {
    // all elements inherit page-level configuration, except MyFree banner
    myFreeDtvBanner: {
      background: "#EEF9FF",
      theme: "onLight"
    }
  },
  packagesTab: {
    header: {
      background: "transparent",
      theme: "onDark"
    },
    sort: {
      background: "transparent",
      theme: "onDark"
    },
    packageTiles: [
      {
        background: "#F0FBFF",
        theme: "onLight"
      },
      {
        background: "#FFFAF6",
        theme: "onLight"
      },
      {
        background: "#E9F1FB",
        theme: "onLight"
      },
      {
        background: "#FEF2FE",
        theme: "onLight"
      }
    ],
    myFreeDtvBanner: {
      background: "#EEF9FF",
      theme: "onLight"
    },
    sectionHeaders: {
      background: "#FFF",
      theme: "onLight"
    },
    grid: {
      background: "transparent",
      theme: "onDark"
    },
    gridAddOnPromos: {
      background: "#21528c",
      theme: "onDark"
    }
  }
};

export const defaultThemingLight = {
  page: {
    backgroundUrl: "",
    backgroundColor: "neutral100", // fallback color
    theme: "onLight"
  },
  categoriesTab: {
    // all follow page theme except my free banner
    myFreeDtvBanner: {
      background: "#EEF9FF",
      theme: "onLight"
    }
  },
  packagesTab: {
    header: {
      background: "transparent",
      theme: "onLight"
    },
    sort: {
      background: "transparent",
      theme: "onLight"
    },
    packageTiles: [
      {
        background: "#ECF3FB",
        theme: "onLight"
      },
      {
        background: "#D8E7F9",
        theme: "onLight"
      },
      {
        background: "#7DB3F2",
        theme: "onLight"
      },
      {
        background: "#196DD4",
        theme: "onLight"
      }
    ],
    myFreeDtvBanner: {
      background: "turquoise50",
      theme: "onLight"
    },
    grid: {
      background: "transparent",
      theme: "onLight"
    },
    sectionHeaders: {
      background: "#ECF3FB",
      theme: "onLight"
    }
  }
};

export const onLightTextColor = palette["text-primary"]; // "#0062E3" // "blue[700]" //
export const onDarkTextColor = palette["text-inverse-primary"];

export interface IChnlLineupThemeUI {
  pageBackgroundUrl?: string;
  pageBackgroundColor?: string;
  headerTextColor?: string;
  subheadingTextColor?: string;
  subheadingLegalTextColor?: string;
  zipCodeModuleMode?: "dark" | "light";
  toggleBtnMode?: "dark" | "light";
  chipsMode?: "dark" | "light";
  footerMode?: "dark" | "light";
  categoriesTab: {
    myFreeDtvBanner: {
      background: string;
      textColor: string;
      border?: string;
    };
    category?: {
      textColor?: string;
      mode?: "dark" | "light";
    };
  };
  packagesTab: {
    isVariablePageBg?: boolean; // for overriding transparent for overflow
    headerScrollHideBg?: string; // bg to show once scrolling, to hide overflow
    headerBackground: string; // color token or hex value
    sort?: {
      background?: string;
      textColor?: string;
      border?: string;
      mode?: "dark" | "light";
    };
    packageTiles?: Array<{
      background?: string; // color token or hex value
      packageNameText?: string; // color token or hex value
      packageChannelsText?: string; // color token or hex value
      border?: string; // color token or hex value
    }>;
    myFreeDtvBanner: {
      background: string; // color token or hex value
      textColor: string; // color token or hex value
      border?: string; // color token or hex value
    };
    sectionHeaders: {
      background: string; // color token or hex value
      textColor: string; // color token or hex value
      border?: string; // color token or hex value
    };
    gridBorder: string; // color token or hex value
    gridBackground: string; // color token or hex value
    gridAddOnPromoBackground: string; // color token or hex value
    gridAddOnPromoText: string; // color token or hex value
    gridText: string; // color token or hex value
    indicatorIcons?: {
      included?: string;
      addon?: string;
      promo?: string;
    };
  };
  scrollToTopBackground?: string;
}

/**
 * @description Parses the content?.theming object from the ChnlListingPage feed
 * @param themeFeed content?.theming
 * @param numPackages number of packages in packages view, to map package tiles array
 * @returns returns a structured theme object for the ChnlListingPageV2 component
 */
export function parseThemeObject(
  themeFeed: any,
  numPackages: number
): IChnlLineupThemeUI {
  const { page, categoriesTab, packagesTab } = themeFeed;

  // Tolerate both theming-feed field names for a slot's color. The canonical
  // defaultTheming* shapes (and transformAEMToSB, flag-OFF) key sub-items
  // `background`; the flag-ON Storyblok blok authors every slot with the reused
  // `page` component, which keys the color `backgroundColor`. By the time this
  // runs the feed is already array-unwrapped (page-adapter's cleanEmpty(arraytoObj))
  // — only the field name differs, so coalesce it here. (The page slot is read
  // via page.backgroundColor below; both shapes already agree on that key.)
  const slotBg = (slot: any) => slot?.background ?? slot?.backgroundColor;

  const normalizeTheme = (theme: string): "dark" | "light" => {
    if (theme === "" || theme === undefined || theme === null) {
      return "dark";
    }
    return ["onlight", "light"].includes(theme?.trim()?.toLowerCase()) ?
      "light" :
      "dark";
  };

  const getThemeText = (theme: string) => {
    if (theme === "" || theme === undefined || theme === null) {
      return onDarkTextColor;
    }
    return normalizeTheme(theme) === "light" ?
      onLightTextColor :
      onDarkTextColor;
  };

  const parseColor = (color: string) => {
    if (color === "" || color === undefined || color === null) {
      return "";
    } else if (palette?.[`${color}`]) {
      return palette?.[`${color}`];
    } else if (
      color?.startsWith("#") ||
      ["rgb", "hsl", "transparent", "none"]?.some(other => color?.toLowerCase()?.includes(other))
    ) {
      return color;
    } // Hex color
    return "";
  };

  try {
    return {
      pageBackgroundUrl: page?.backgroundUrl,
      pageBackgroundColor: parseColor(page?.backgroundColor),
      headerTextColor: getThemeText(page?.theme),
      subheadingTextColor: getThemeText(page?.theme),
      subheadingLegalTextColor: getThemeText(page?.theme),
      zipCodeModuleMode: normalizeTheme(page?.theme),
      toggleBtnMode: normalizeTheme(page?.theme),
      chipsMode: normalizeTheme(page?.theme),
      footerMode: normalizeTheme(page?.theme),
      categoriesTab: {
        myFreeDtvBanner: {
          background: parseColor(slotBg(categoriesTab?.myFreeDtvBanner)),
          textColor: getThemeText(categoriesTab?.myFreeDtvBanner?.theme),
          border: categoriesTab?.myFreeDtvBanner?.border || "none"
        },
        category: {
          textColor: getThemeText(page?.theme),
          mode: normalizeTheme(page?.theme)
        }
      },
      packagesTab: {
        // for overriding transparent for overflow
        isVariablePageBg: page?.backgroundUrl !== "",
        // bg to show once scrolling, to hide overflow
        headerScrollHideBg:
          !slotBg(packagesTab?.header) ||
          slotBg(packagesTab?.header) === "transparent" ?
            parseColor(page?.backgroundColor) :
            parseColor(slotBg(packagesTab?.header)),
        headerBackground:
          parseColor(slotBg(packagesTab?.header)) || "transparent",
        sort: {
          background:
            parseColor(slotBg(packagesTab?.sort)) || "transparent",
          textColor: getThemeText(
            packagesTab?.sort?.theme ||
              packagesTab?.header?.theme ||
              page?.theme
          ),
          border: packagesTab?.sort?.border || "none",
          mode: normalizeTheme(
            packagesTab?.sort?.theme ||
              packagesTab?.header?.theme ||
              page?.theme
          )
        },
        packageTiles: Array.from(Array(numPackages)).map((_, ix) => ({
          background:
              parseColor(slotBg(packagesTab?.packageTiles?.[ix])) ||
              "transparent",
          packageNameText: getThemeText(
            packagesTab?.packageTiles?.[ix]?.theme ||
                packagesTab?.header?.theme ||
                page?.theme
          ),
          packageChannelsText: getThemeText(
            packagesTab?.packageTiles?.[ix]?.theme ||
                packagesTab?.header?.theme ||
                page?.theme
          ),
          border: packagesTab?.packageTiles?.[ix]?.border || "none"
        })),
        myFreeDtvBanner: {
          background: parseColor(slotBg(packagesTab?.myFreeDtvBanner)),
          textColor: getThemeText(packagesTab?.myFreeDtvBanner?.theme),
          border: packagesTab?.myFreeDtvBanner?.border || "none"
        },
        sectionHeaders: {
          background: "#ECF3FB",
          textColor: getThemeText(
            packagesTab?.sectionHeaders?.theme || page?.theme
          ),
          border: packagesTab?.sectionHeaders?.border || "#D1D1D1"
        },
        gridBorder:
          normalizeTheme(packagesTab?.grid?.theme || page?.theme) === "dark" ?
            palette.neutral600 :
            palette.neutral300,
        gridBackground:
          parseColor(slotBg(packagesTab?.grid)) || "transparent",
        gridText: getThemeText(packagesTab?.grid?.theme || page?.theme),
        gridAddOnPromoBackground:
          parseColor(slotBg(packagesTab?.gridAddOnPromos)) || "transparent",
        gridAddOnPromoText: getThemeText(
          packagesTab?.gridAddOnPromos?.theme || page?.theme
        ),
        indicatorIcons: {
          included:
            palette[
              normalizeTheme(packagesTab?.grid?.theme || page?.theme) === "dark" ?
                "green500" :
                "green600"
            ],
          addon:
            palette[
              normalizeTheme(packagesTab?.grid?.theme || page?.theme) === "dark" ?
                "magenta500" :
                "magenta600"
            ],
          promo:
            palette[
              normalizeTheme(packagesTab?.grid?.theme || page?.theme) === "dark" ?
                "blue500" :
                "blue600"
            ]
        }
      },
      scrollToTopBackground:
        normalizeTheme(page?.theme) === "dark" ? "#FFF" : "#102641"
    };
  } catch (error) {
    console.error("[ChnlListingPage] ERROR parsing theme object:", error);
    return parseThemeObject(defaultThemingDark, numPackages);
  }
}
